import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, ArrowRight, ShieldCheck, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const checks = [
  { id: "len", label: "Mínimo 8 caracteres", test: (p) => p.length >= 8 },
  { id: "upper", label: "Una letra mayúscula", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "Una letra minúscula", test: (p) => /[a-z]/.test(p) },
  { id: "num", label: "Un número", test: (p) => /\d/.test(p) },
];

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError("Enlace no válido. Solicita un nuevo enlace de recuperación.");
  }, [token]);

  const passed = checks.map((c) => ({ ...c, ok: c.test(next) }));
  const allOk = passed.every((c) => c.ok) && next === confirm;
  const strength = passed.filter((c) => c.ok).length;
  const strengthColor =
    strength <= 1 ? "bg-red-400" : strength <= 2 ? "bg-amber-400" : strength <= 3 ? "bg-yellow-300" : "bg-emerald-400";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Enlace no válido.");
      return;
    }
    if (!allOk) {
      setError("Revisa los requisitos de la contraseña.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: next });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-[#04140a] via-[#06150c] to-[#091f12] relative overflow-hidden text-white">
      <div className="absolute -top-32 -right-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-green-700/10 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative z-10"
        data-testid="reset-password-page"
      >
        <div className="rounded-3xl p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]"
             style={{
               background: "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)",
               backdropFilter: "blur(20px) saturate(140%)",
               border: "1px solid rgba(74,222,128,0.25)",
             }}>
          {!success ? (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <KeyRound size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-extrabold tracking-tight">Crear nueva contraseña</h1>
                  <p className="text-emerald-100/60 text-xs">Te enviamos este enlace por email</p>
                </div>
              </div>

              {error && (
                <div data-testid="reset-password-error" className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-xl text-red-300 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-emerald-100/80 mb-2 tracking-wider uppercase">Nueva contraseña</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-300/70" />
                    <input
                      data-testid="reset-new-password-input"
                      type={show ? "text" : "password"}
                      value={next}
                      onChange={(e) => setNext(e.target.value)}
                      className="w-full pl-10 pr-10 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/60"
                      placeholder="Mínimo 8 caracteres"
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShow(!show)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-300/70 hover:text-emerald-300"
                    >
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`h-1 flex-1 rounded-full ${i < strength ? strengthColor : "bg-white/10"} transition-colors`} />
                    ))}
                  </div>
                  <ul className="mt-3 grid grid-cols-2 gap-1.5 text-[11px]">
                    {passed.map((c) => (
                      <li key={c.id} className={`flex items-center gap-1.5 ${c.ok ? "text-emerald-300" : "text-emerald-100/40"}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.ok ? "bg-emerald-400" : "bg-white/30"}`} />
                        {c.label}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label className="block text-xs font-bold text-emerald-100/80 mb-2 tracking-wider uppercase">Confirmar contraseña</label>
                  <div className="relative">
                    <ShieldCheck size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-300/70" />
                    <input
                      data-testid="reset-confirm-password-input"
                      type={show ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/60"
                      placeholder="Repite la nueva contraseña"
                      required
                    />
                  </div>
                  {confirm && next !== confirm && (
                    <p className="mt-1.5 text-[11px] text-red-300">Las contraseñas no coinciden.</p>
                  )}
                </div>

                <motion.button
                  whileTap={{ scale: 0.97 }}
                  data-testid="reset-password-submit"
                  type="submit"
                  disabled={loading || !allOk || !token}
                  className="w-full flex items-center justify-center gap-2 mt-2
                             bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400
                             text-white font-bold py-3.5 rounded-xl transition-all
                             shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Restablecer contraseña <ArrowRight size={16} /></>
                  )}
                </motion.button>
              </form>

              <Link
                to="/login"
                className="mt-5 inline-flex items-center gap-1.5 text-xs text-emerald-300/70 hover:text-emerald-300"
              >
                <ArrowLeft size={12} /> Volver al login
              </Link>
            </>
          ) : (
            <div className="text-center py-4" data-testid="reset-password-success">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.05 }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 mb-4"
              >
                <CheckCircle2 size={32} className="text-emerald-300" />
              </motion.div>
              <h3 className="text-xl font-extrabold mb-2">Contraseña actualizada</h3>
              <p className="text-sm text-emerald-100/70 leading-relaxed mb-6">
                Tu contraseña se ha guardado. Ya puedes iniciar sesión en la plataforma con la nueva.
              </p>
              <button
                onClick={() => navigate("/login")}
                data-testid="reset-password-go-login"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5
                           bg-gradient-to-r from-emerald-500 to-green-500
                           text-white font-bold rounded-xl
                           shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]"
              >
                Ir al login <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-emerald-100/40 mt-6">
          Valoriza Innovation Team · valorizasm.com
        </p>
      </motion.div>
    </div>
  );
}
